/**
 * pages/AdminUsers.tsx — the user-administration module.
 *
 * The module contract is a set of QUESTIONS, not a set of columns, and this screen is arranged to answer
 * them without a second click: who has access, what can each person reach, who changed that and when, who
 * has gone quiet, and how to cut someone off right now.
 *
 * Three things are worth stating because they are easy to get wrong:
 *
 * **There is no pending queue here, deliberately** (`design-deviations.md` D1). Ticketless has no approval
 * step: an account is created `pending` by a first sign-in and an administrator admits it directly from
 * the roster. Building a separate queue region would imply a workflow that does not exist and would hide
 * pending people from the one list that is supposed to be everybody.
 *
 * **Group membership is fetched separately and may be unavailable.** Membership lives behind the *access*
 * section, not the *users* section, so an administrator holding one and not the other sees the roster
 * without the groups column populated. That case says so in the column rather than rendering an empty cell
 * — "no groups" and "you may not see the groups" are different facts, and merging them is how a reviewer
 * concludes somebody has no access when they have plenty.
 *
 * **The business-unit column is the answer to "what can this person reach".** A person belongs to exactly
 * one unit, and the column says which — or says plainly that they are unassigned and therefore reach
 * nothing. Changing it is the superuser's alone (`canAssign`), which is not an oversight: an administrator
 * who could move a person between units could move themselves, and every isolation control in the
 * application would then rest on their restraint rather than on a rule.
 *
 * **Releasing an identity is the way out of a collision, and it is the superuser's.** `linkSubject` refuses
 * to rebind an account that already carries a different IdP subject — the fix for an account takeover — and
 * *Release identity* is the remedy that refusal needs, since without one the only resolution is a database
 * edit. It is gated on `canAssign` rather than on `canAct` because deciding who an account belongs to is a
 * larger act than admitting somebody, and its dialogue states the one case where it is the wrong answer:
 * an address reissued to a NEW person, where releasing hands them the previous holder's access.
 *
 * **A superuser is marked, and it changes three cells rather than adding one.** `06-modules.md` §1.5.3
 * makes the marking a MUST — a superuser sits outside the grant engine, so it is a different kind of
 * fact from a granted role and removing every group will not remove it. Until the field existed this
 * screen told an access reviewer three separate untruths about such an account: its rights column read
 * *"None — this account can reach nothing"*, its business-unit cell read *"Not assigned — reaches
 * nothing"*, and an *Assign unit* button sat beside that cell which the server refuses outright
 * (`ConfinementWriteError`). All three are closed below. The field is a projection of `ADMIN_EMAILS`
 * computed per request, never a stored column — `CLAUDE.md` §1 forbids the latter and is right to.
 *
 * **A superuser may nonetheless CARRY an assignment, and this screen is the only place it can be lifted.**
 * The `PUT` refuses to write one for a superuser; the `DELETE` does not, and neither refuses to *keep* a
 * row written before the address was added to `ADMIN_EMAILS`. That order of events is ordinary — assign
 * Dana to Acme, then promote her — and it used to leave a bound that was both invisible (the cell said
 * only *"Every unit — superuser"* and never named the unit) and permanent (the sole client path to the
 * `DELETE` was the *Remove assignment* button inside the assignment dialogue, which this screen stopped
 * opening for such a row). It is dormant while she is a superuser and binds again the moment she leaves
 * `ADMIN_EMAILS` — silently, months later, by a rule nobody could see. So the cell names the unit and the
 * actions carry the one control that clears it.
 *
 * **Revocation is two different acts and both are offered.** Suspending ends the account; bumping the
 * session version ends every *session* while leaving the account intact — the lost-laptop case. Both take
 * effect immediately on the server (the version is folded into the session AND the CSRF token), which is
 * exactly why both are behind a confirmation: they are instant and they are visible to the person at the
 * other end.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api, type AdminUser, type UserStatus } from "../lib/api";
import { formatDateTime, formatRelative, humanise } from "../lib/format";
import {
  Badge,
  Banner,
  Button,
  DeniedState,
  ErrorState,
  LoadingState,
  Modal,
  Page,
  Select,
  Table,
  TextField,
  useToast,
  type Column,
  type Tone,
} from "../components/ui";

/* ---------------------------------------------------------------------------------------------------
 * Wire shapes — `server/routes/admin.ts`'s `userView`, plus the raw group rows that route returns
 * ------------------------------------------------------------------------------------------------- */

/** `AdminUser` from `lib/api`, plus the two fields this screen needs and that one does not declare. */
export interface RosterUser extends AdminUser {
  readonly admittedBy: string | null;
  readonly sessionVersion: number;
}

interface UserListResponse {
  readonly users: readonly RosterUser[];
  readonly total: number;
}

export interface GroupSummary {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
}

/** `listGroupMembers` is returned unmapped, so these are the database's own column names. */
interface GroupMemberRow {
  readonly group_id: string;
  readonly user_id: string;
  readonly added_at: string;
  readonly added_by: string | null;
}

interface GroupDetailResponse {
  readonly group: GroupSummary;
  readonly members: readonly GroupMemberRow[];
}

export interface BusinessUnitSummary {
  readonly id: string;
  readonly slug: string;
  readonly displayName: string;
  readonly enabled: boolean;
}

export interface RosterData {
  readonly users: readonly RosterUser[];
  /** Null when the caller may not read groups — distinct from an empty map, which means "no groups". */
  readonly membership: ReadonlyMap<string, readonly string[]> | null;
  readonly groups: readonly GroupSummary[];
  /** Null when the caller may not read business units. Names are a courtesy; ids are shown regardless. */
  readonly businessUnits: readonly BusinessUnitSummary[] | null;
}

export interface AdminUsersPorts {
  readonly load: (signal: AbortSignal) => Promise<RosterData>;
  readonly admit: (userId: string) => Promise<void>;
  readonly suspend: (userId: string) => Promise<void>;
  readonly bumpSession: (userId: string) => Promise<void>;
  readonly assign: (userId: string, buId: string) => Promise<void>;
  readonly unassign: (userId: string) => Promise<void>;
  /** Unbind this account from the identity it currently carries. Superuser-only on the server. */
  readonly releaseIdentity: (userId: string) => Promise<void>;
}

async function loadRoster(signal: AbortSignal): Promise<RosterData> {
  const list = await api.get<UserListResponse>("/api/admin/users", { signal });

  // Membership is a best effort: it belongs to another section's grant, and being refused it is a normal
  // configuration rather than a failure of this screen.
  let groups: readonly GroupSummary[] = [];
  let membership: Map<string, string[]> | null = null;
  try {
    const groupList = await api.get<{ readonly groups: readonly GroupSummary[] }>("/api/admin/groups", { signal });
    groups = groupList.groups;
    membership = new Map<string, string[]>();
    const details = await Promise.all(
      groups.map((group) =>
        api.get<GroupDetailResponse>(`/api/admin/groups/${encodeURIComponent(group.id)}`, { signal }),
      ),
    );
    for (const detail of details) {
      for (const member of detail.members) {
        const current = membership.get(member.user_id);
        if (current === undefined) membership.set(member.user_id, [detail.group.name]);
        else current.push(detail.group.name);
      }
    }
  } catch {
    membership = null;
  }

  // Business-unit names, on the same best-effort footing as membership and for the same reason: reading
  // them belongs to another section's grant, and being refused is a configuration rather than a fault.
  let businessUnits: readonly BusinessUnitSummary[] | null = null;
  try {
    const list = await api.get<{ readonly businessUnits: readonly BusinessUnitSummary[] }>(
      "/api/admin/business-units",
      { signal },
    );
    businessUnits = list.businessUnits;
  } catch {
    businessUnits = null;
  }

  return { users: list.users, membership, groups, businessUnits };
}

const apiPorts: AdminUsersPorts = {
  load: loadRoster,
  admit: async (userId) => {
    await api.post(`/api/admin/users/${encodeURIComponent(userId)}/admit`, {});
  },
  suspend: async (userId) => {
    await api.post(`/api/admin/users/${encodeURIComponent(userId)}/suspend`, {});
  },
  bumpSession: async (userId) => {
    await api.post(`/api/admin/users/${encodeURIComponent(userId)}/bump-session`, {});
  },
  assign: async (userId, buId) => {
    await api.put(`/api/admin/users/${encodeURIComponent(userId)}/business-unit`, { buId });
  },
  unassign: async (userId) => {
    await api.delete(`/api/admin/users/${encodeURIComponent(userId)}/business-unit`);
  },
  releaseIdentity: async (userId) => {
    await api.post(`/api/admin/users/${encodeURIComponent(userId)}/identity`, { subject: null });
  },
};

/* ---------------------------------------------------------------------------------------------------
 * The screen
 * ------------------------------------------------------------------------------------------------- */

const STATUS_TONE: Readonly<Record<UserStatus, Tone>> = {
  active: "success",
  pending: "warning",
  suspended: "danger",
};

/**
 * `unassign` is the odd one out: it is confirmed here but carried out by `assign(user, null)` rather than
 * by `act`, because clearing an assignment is the same write as changing one and must not become a second
 * caller of the same route.
 */
type PendingAction = {
  readonly kind: "suspend" | "bump" | "release" | "unassign";
  readonly user: RosterUser;
};

/** The assignment dialogue's own state: which person, and what the administrator has chosen so far. */
type PendingAssignment = { readonly user: RosterUser; readonly buId: string };

/** The filter's own vocabulary, kept as a value so the select can narrow a string without a cast. */
const STATUS_FILTERS = ["all", "pending", "active", "suspended"] as const;

export interface AdminUsersProps {
  /** From `/api/me`. Drawing the screen without it would produce a refusal the reader cannot act on. */
  readonly canView: boolean;
  readonly canAct: boolean;
  /**
   * Whether the viewer may CHANGE a business-unit assignment — the superuser, and nobody else. This is a
   * hard rail rather than a section: a delegated administrator who could move a person between units
   * could move themselves, and the whole isolation story would rest on their good manners. See
   * `server/permissions/delegation.ts`.
   */
  readonly canAssign?: boolean;
  /** True when the deployment requires an assignment, which is what makes "not assigned" mean "nothing". */
  readonly requiresBusinessUnit?: boolean;
  readonly ports?: AdminUsersPorts;
}

export function AdminUsers({
  canView,
  canAct,
  canAssign = false,
  requiresBusinessUnit = true,
  ports = apiPorts,
}: AdminUsersProps): JSX.Element {
  const [data, setData] = useState<RosterData | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<PendingAction | null>(null);
  const [assigning, setAssigning] = useState<PendingAssignment | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | UserStatus>("all");
  const toast = useToast();

  const reload = useCallback((): void => setAttempt((current) => current + 1), []);

  useEffect(() => {
    if (!canView) return;
    const controller = new AbortController();
    let live = true;
    setData(null);
    setError(null);

    ports
      .load(controller.signal)
      .then((loaded) => {
        if (live) setData(loaded);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null));
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [ports, canView, attempt]);

  const act = useCallback(
    (kind: "admit" | "suspend" | "bump" | "release", user: RosterUser): void => {
      setBusy(user.id);
      const work =
        kind === "admit"
          ? ports.admit(user.id)
          : kind === "suspend"
            ? ports.suspend(user.id)
            : kind === "bump"
              ? ports.bumpSession(user.id)
              : ports.releaseIdentity(user.id);

      work
        .then(() => {
          toast.show(
            kind === "admit"
              ? `${user.email} admitted`
              : kind === "suspend"
                ? `${user.email} suspended and signed out everywhere`
                : kind === "bump"
                  ? `${user.email} signed out of every session`
                  : `${user.email} is no longer bound to a sign-in identity`,
            { tone: "success" },
          );
          reload();
        })
        .catch((cause: unknown) => {
          const failure = cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
          toast.show(failure.userMessage, {
            tone: "danger",
            ...(failure.traceId === null ? {} : { detail: `Trace ${failure.traceId}` }),
          });
        })
        .finally(() => {
          setBusy(null);
          setConfirm(null);
        });
    },
    [ports, reload, toast],
  );

  /**
   * Assign or release, then reload. Deliberately NOT optimistic: this changes what somebody can reach,
   * and a screen that shows the new unit before the server has agreed is a screen that will eventually
   * show an assignment that was refused.
   */
  const assign = useCallback(
    (user: RosterUser, buId: string | null): void => {
      setBusy(user.id);
      const work = buId === null ? ports.unassign(user.id) : ports.assign(user.id, buId);
      work
        .then(() => {
          toast.show(
            buId === null
              ? `${user.email} is no longer assigned to a business unit`
              : `${user.email} assigned to ${buId}`,
            { tone: "success" },
          );
          reload();
        })
        .catch((cause: unknown) => {
          const failure = cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
          toast.show(failure.userMessage, {
            tone: "danger",
            ...(failure.traceId === null ? {} : { detail: `Trace ${failure.traceId}` }),
          });
        })
        .finally(() => {
          setBusy(null);
          setAssigning(null);
          // Cleared here too: a superuser's dormant assignment is lifted from the confirmation dialogue
          // rather than from the assignment one, and leaving it open would sit over the reloaded roster.
          setConfirm(null);
        });
    },
    [ports, reload, toast],
  );

  const users = data?.users ?? [];
  const membership = data?.membership ?? null;
  const businessUnits = data?.businessUnits ?? null;

  const buNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const bu of businessUnits ?? []) names.set(bu.id, bu.displayName);
    return names;
  }, [businessUnits]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users.filter((user) => {
      if (status !== "all" && user.status !== status) return false;
      if (needle === "") return true;
      return (
        user.email.toLowerCase().includes(needle) ||
        (user.displayName ?? "").toLowerCase().includes(needle) ||
        (membership?.get(user.id) ?? []).some((group) => group.toLowerCase().includes(needle))
      );
    });
  }, [users, membership, search, status]);

  const emailById = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of users) map.set(user.id, user.email);
    return map;
  }, [users]);

  if (!canView) {
    return (
      <Page title="People">
        <DeniedState body="Administering people is its own grant. Ask an administrator to add you to a group that carries the users section." />
      </Page>
    );
  }

  const counts = {
    total: users.length,
    pending: users.filter((user) => user.status === "pending").length,
    suspended: users.filter((user) => user.status === "suspended").length,
    never: users.filter((user) => !user.linked).length,
  };

  const columns: readonly Column<RosterUser>[] = [
    {
      key: "email",
      header: "Email",
      type: "text",
      value: (user) => user.email,
      // Two spans with nothing between them rendered as "kevork@centro.teamKevork A" — no separator, no
      // line break, the address and the name run into one unreadable string. `identity` stacks them, which
      // is what the smaller, muted second line was always meant to be.
      render: (user) => (
        <span className="identity">
          <span className="mono">{user.email}</span>
          {user.displayName === null ? null : <span className="field__hint">{user.displayName}</span>}
        </span>
      ),
    },
    {
      key: "status",
      header: "Admission",
      type: "text",
      value: (user) => user.status,
      render: (user) => <Badge tone={STATUS_TONE[user.status]}>{humanise(user.status)}</Badge>,
    },
    {
      key: "confinedTo",
      header: "Business unit",
      type: "text",
      // Sorted on the name shown, so unassigned accounts gather at one end of the sort rather than
      // scattering — they are the ones an administrator is looking for.
      value: (user) =>
        user.isSuperuser
          ? user.confinedTo === null
            ? "Every unit"
            : `Every unit — dormant assignment to ${buNames.get(user.confinedTo) ?? user.confinedTo}`
          : user.confinedTo === null
            ? null
            : (buNames.get(user.confinedTo) ?? user.confinedTo),
      render: (user) => {
        // A superuser reaches every enabled unit whatever this row says: the bound is applied where
        // `accessibleScopes` is computed and the resolver short-circuits above it. Rendering a null
        // `confinedTo` as "reaches nothing" was the opposite of the truth.
        //
        // ⚠️ But `confinedTo` is NOT always null for them, and the comment that used to say so here was
        // wrong. The assignment route refuses to WRITE one for a superuser; nothing removes a row that
        // was written before the address entered `ADMIN_EMAILS`. Such a row is dormant rather than
        // absent — and it binds again the day they leave `ADMIN_EMAILS` — so it is named rather than
        // hidden behind the badge that made it look like there was nothing to see.
        if (user.isSuperuser) {
          if (user.confinedTo === null) return <Badge tone="warning">Every unit — superuser</Badge>;
          return (
            <span className="row">
              <Badge tone="warning">Every unit — superuser</Badge>
              <span className="field__hint">
                Still assigned to {buNames.get(user.confinedTo) ?? user.confinedTo} — dormant now, and
                binding again if this address leaves ADMIN_EMAILS
              </span>
            </span>
          );
        }
        if (user.confinedTo === null) {
          return (
            <span className="field__hint">
              {requiresBusinessUnit ? "Not assigned — reaches nothing" : "Not assigned"}
            </span>
          );
        }
        return <Badge>{buNames.get(user.confinedTo) ?? user.confinedTo}</Badge>;
      },
    },
    {
      key: "groups",
      header: "Groups",
      type: "text",
      // Sorted on the joined names so the column orders by what it shows; a count would order by a number
      // the reader cannot see. The superuser chip is part of what it shows, so it is part of the sort.
      value: (user) =>
        membership === null
          ? null
          : [...(user.isSuperuser ? ["Superuser"] : []), ...(membership.get(user.id) ?? [])].join(", "),
      render: (user) => {
        if (membership === null) return <span className="field__hint">Not visible to you</span>;
        const names = membership.get(user.id) ?? [];
        // §1.5.3: the superuser mark is its own chip, alongside the sources and distinct from them —
        // it is conferred by no group, and it would survive the removal of every one of them.
        const mark = user.isSuperuser ? <Badge tone="warning">Superuser</Badge> : null;
        if (names.length === 0) {
          return user.isSuperuser ? (
            <span className="row">
              {mark}
              <span className="field__hint">No groups — reach comes from ADMIN_EMAILS</span>
            </span>
          ) : (
            "None — this account can reach nothing"
          );
        }
        return (
          <span className="row">
            {mark}
            {names.map((name) => (
              <Badge key={name}>{name}</Badge>
            ))}
          </span>
        );
      },
    },
    {
      key: "lastSeenAt",
      header: "Last seen",
      type: "date",
      value: (user) => user.lastSeenAt,
      render: (user) =>
        user.lastSeenAt === null ? (
          <span title="This account has never completed a sign-in">Never</span>
        ) : (
          <time title={formatDateTime(user.lastSeenAt)}>{formatRelative(user.lastSeenAt)}</time>
        ),
    },
    {
      key: "admittedAt",
      header: "Admitted",
      type: "date",
      value: (user) => user.admittedAt,
      render: (user) =>
        user.admittedAt === null ? (
          "—"
        ) : (
          <time
            title={`${formatDateTime(user.admittedAt)} by ${
              user.admittedBy === null ? "an administrator no longer on record" : (emailById.get(user.admittedBy) ?? user.admittedBy)
            }`}
          >
            {formatRelative(user.admittedAt)} by{" "}
            {user.admittedBy === null ? "unknown" : (emailById.get(user.admittedBy) ?? user.admittedBy)}
          </time>
        ),
      hideOnMobile: true,
    },
    {
      key: "linked",
      header: "Signed in before",
      type: "boolean",
      value: (user) => user.linked,
      render: (user) => (user.linked ? "Yes" : "Never"),
      hideOnMobile: true,
    },
    {
      key: "actions",
      actions: true,
      header: "Actions",
      sortable: false,
      render: (user) =>
        !canAct && !canAssign ? (
          <span className="field__hint">View only</span>
        ) : (
          <span className="row">
            {/*
              `POST /users/:id/business-unit` refuses a superuser outright — their reach is derived from
              ADMIN_EMAILS outside the engine and no bound narrows it — so this button always 403'd on
              such a row. A control that cannot work is replaced by the sentence saying why, which is
              this repository's own rule about a button that would always 403.
            */}
            {!canAssign ? null : user.isSuperuser ? (
              <span className="row">
                <span className="field__hint">
                  Not assignable — a superuser&apos;s reach comes from ADMIN_EMAILS
                </span>
                {/*
                  Two different facts, and only the first was ever said. A superuser cannot be GIVEN an
                  assignment — the `PUT` refuses — but one written before they entered `ADMIN_EMAILS`
                  survives, and the `DELETE` still accepts it. Without this control the bound could not
                  be lifted from anywhere in the application, and it would silently take effect again
                  the day the address left `ADMIN_EMAILS`.
                */}
                {user.confinedTo === null ? null : (
                  <Button
                    variant="danger"
                    size="sm"
                    loading={busy === user.id}
                    title="Lift the dormant business-unit assignment this account still carries"
                    onClick={() => setConfirm({ kind: "unassign", user })}
                  >
                    Remove assignment
                  </Button>
                )}
              </span>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                loading={busy === user.id}
                title="Choose the one business unit this person belongs to"
                onClick={() => setAssigning({ user, buId: user.confinedTo ?? "" })}
              >
                {user.confinedTo === null ? "Assign unit" : "Change unit"}
              </Button>
            )}
            {!canAct || user.status === "active" ? null : (
              <Button
                variant="secondary"
                size="sm"
                loading={busy === user.id}
                onClick={() => act("admit", user)}
              >
                {user.status === "suspended" ? "Reinstate" : "Admit"}
              </Button>
            )}
            {!canAct || user.status === "suspended" ? null : (
              <Button
                variant="danger"
                size="sm"
                loading={busy === user.id}
                onClick={() => setConfirm({ kind: "suspend", user })}
              >
                Suspend
              </Button>
            )}
            {!canAct ? null : (
              <Button
                variant="ghost"
                size="sm"
                loading={busy === user.id}
                title="End every session this person has, everywhere, immediately"
                onClick={() => setConfirm({ kind: "bump", user })}
              >
                Sign out everywhere
              </Button>
            )}
            {/*
              The way out of an identity collision, and the superuser's alone — `canAssign` is this
              screen's superuser flag, and unbinding decides WHO an account belongs to, which is a larger
              act than assigning a unit. Offered only where there is an identity to release: on an account
              that has never signed in the button would do nothing and mean nothing.
            */}
            {!canAssign || !user.linked ? null : (
              <Button
                variant="ghost"
                size="sm"
                loading={busy === user.id}
                title="Unbind this account from the sign-in identity it currently carries"
                onClick={() => setConfirm({ kind: "release", user })}
              >
                Release identity
              </Button>
            )}
          </span>
        ),
    },
  ];

  return (
    <Page
      title="People"
      lede="Existence grants nothing. An account is created by its first sign-in and can do nothing at all until an administrator admits it and a group carries it into a business unit."
    >
      <div className="row">
        <Badge>{counts.total} accounts</Badge>
        <Badge tone={counts.pending === 0 ? "neutral" : "warning"}>{counts.pending} awaiting admission</Badge>
        <Badge tone={counts.suspended === 0 ? "neutral" : "danger"}>{counts.suspended} suspended</Badge>
        <Badge>{counts.never} never signed in</Badge>
      </div>

      {membership === null && data !== null ? (
        <Banner tone="info">
          Group membership is not shown: reading it needs the access section, which this account does not
          hold. Everything else on this screen is complete.
        </Banner>
      ) : null}

      <div className="row">
        <TextField
          label="Search"
          value={search}
          onChange={setSearch}
          type="search"
          placeholder="Email, name or group"
        />
        <Select
          label="Admission status"
          value={status}
          options={[
            { value: "all", label: "All statuses" },
            { value: "pending", label: "Awaiting admission" },
            { value: "active", label: "Admitted" },
            { value: "suspended", label: "Suspended" },
          ]}
          onChange={(value) => {
            const match = STATUS_FILTERS.find((candidate) => candidate === value);
            setStatus(match ?? "all");
          }}
        />
      </div>

      {error !== null ? (
        error.isPermissionDenied ? (
          <DeniedState body={error.userMessage} />
        ) : (
          <ErrorState message={error.userMessage} traceId={error.traceId} onRetry={reload} />
        )
      ) : null}

      {error === null && data === null ? <LoadingState label="Loading people" /> : null}

      {data !== null ? (
        <Table
          caption="People"
          columns={columns}
          rows={filtered}
          rowKey={(user) => user.id}
          defaultSort={{ key: "email", direction: "ascending" }}
          titleKey="email"
          mobile="cards"
          empty={{
            title: users.length === 0 ? "No accounts yet" : "No accounts match this filter",
            body:
              users.length === 0
                ? "Accounts are created the first time somebody signs in, and arrive awaiting admission. Nobody has signed in yet."
                : "Clear the search or choose a different admission status.",
          }}
        />
      ) : null}

      <Modal
        open={confirm !== null}
        role="alertdialog"
        title={
          confirm?.kind === "suspend"
            ? "Suspend this account?"
            : confirm?.kind === "release"
              ? "Release this account's identity?"
              : confirm?.kind === "unassign"
                ? "Lift this dormant business-unit assignment?"
                : "Sign this person out everywhere?"
        }
        description={
          confirm === null
            ? ""
            : confirm.kind === "suspend"
              ? `${confirm.user.email} will be unable to sign in, and every session they currently hold ends immediately.`
              : confirm.kind === "release"
                ? `${confirm.user.email} will be unbound from the sign-in identity it carries, and every session it holds ends immediately.`
                : confirm.kind === "unassign"
                  ? `${confirm.user.email} is assigned to ${
                      confirm.user.confinedTo === null
                        ? "a business unit"
                        : (buNames.get(confirm.user.confinedTo) ?? confirm.user.confinedTo)
                    }. It changes nothing while they are a superuser, and would narrow them to that one unit the moment their address leaves ADMIN_EMAILS. Nothing else about their access changes.`
                  : `${confirm.user.email} keeps their access, but every session and every open tab stops working at once and they must sign in again.`
        }
        onClose={() => setConfirm(null)}
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant={confirm?.kind === "bump" ? "primary" : "danger"}
              loading={busy !== null}
              onClick={() => {
                if (confirm === null) return;
                // `unassign` is the same write as changing an assignment, so it goes through `assign`
                // rather than becoming a second caller of the confinement route.
                if (confirm.kind === "unassign") assign(confirm.user, null);
                else act(confirm.kind, confirm.user);
              }}
            >
              {confirm?.kind === "suspend"
                ? "Suspend"
                : confirm?.kind === "release"
                  ? "Release identity"
                  : confirm?.kind === "unassign"
                    ? "Remove assignment"
                    : "Sign out everywhere"}
            </Button>
          </>
        }
      >
        {confirm?.kind === "release" ? (
          <>
            <p>
              Use this when the identity provider has issued this person a new sign-in identity — a tenant
              migration or a directory rebuild — and they can no longer get in. The account keeps its
              groups, its business unit and its updates.
            </p>
            {/*
              Said out loud because the screen cannot tell the two cases apart and the administrator can.
              Releasing an account whose ADDRESS has been reissued to somebody else hands them everything
              the previous holder had — which is precisely the takeover the refusal exists to prevent.
            */}
            <Banner tone="warning">
              The next person to sign in with this email address will take over this account. If the
              address has been reassigned to a new joiner, do not release it — they need an account of
              their own.
            </Banner>
          </>
        ) : (
          <p>This takes effect immediately. It is recorded against your name in the policy trail.</p>
        )}
      </Modal>

      <Modal
        open={assigning !== null}
        title="Which business unit does this person belong to?"
        description={
          assigning === null
            ? ""
            : `${assigning.user.email} will be able to reach one business unit and no other. This assigns nothing on its own — they still need a group that carries access to that unit.`
        }
        onClose={() => setAssigning(null)}
        actions={
          <>
            <Button variant="ghost" onClick={() => setAssigning(null)}>
              Cancel
            </Button>
            {assigning?.user.confinedTo === undefined || assigning?.user.confinedTo === null ? null : (
              <Button
                variant="danger"
                loading={busy !== null}
                title={
                  requiresBusinessUnit
                    ? "Removing the assignment leaves this person reaching no business unit at all"
                    : "Removing the assignment lets this person reach every unit their groups grant"
                }
                onClick={() => {
                  if (assigning !== null) assign(assigning.user, null);
                }}
              >
                Remove assignment
              </Button>
            )}
            <Button
              variant="primary"
              loading={busy !== null}
              disabled={assigning === null || assigning.buId === ""}
              onClick={() => {
                if (assigning !== null && assigning.buId !== "") assign(assigning.user, assigning.buId);
              }}
            >
              Assign
            </Button>
          </>
        }
      >
        {businessUnits === null ? (
          <Banner tone="warning">
            The list of business units is not visible to this account, so there is nothing to choose from
            here.
          </Banner>
        ) : (
          <Select
            label="Business unit"
            value={assigning?.buId ?? ""}
            options={[
              { value: "", label: "Choose a business unit" },
              ...businessUnits.map((bu) => ({
                value: bu.id,
                // A disabled unit is invisible to everyone, so assigning somebody to one leaves them
                // reaching nothing. Legal, occasionally deliberate, and said out loud either way.
                label: bu.enabled ? bu.displayName : `${bu.displayName} (disabled — reaches nothing)`,
              })),
            ]}
            onChange={(value) => {
              setAssigning((current) => (current === null ? null : { ...current, buId: value }));
            }}
          />
        )}
        <p>
          A person belongs to exactly one business unit. Changing it here replaces the previous one; there
          is no way to be in two.
        </p>
      </Modal>
    </Page>
  );
}
